import { __read } from "tslib";
import { DirectionalHint, Link, TeachingBubble, Text, } from '@fluentui/react';
import { useCurrentRoute, useOfficeComponentContext, } from '@office-start/component-context-service';
import { useFirstRunVisibilityStore } from '@office-start/first-run-component';
import { updateTuiNewUserState } from '@office-start/first-run-state-service';
import { useIsMounted } from '@office-start/hooks-utility';
import { OfficeWorkload } from '@office-start/officehome-types';
import { useShellShim } from '@office-start/shell-shim-service';
import { useTeachingUiConfig } from '@office-start/teaching-ui-config-service';
import { useSingletonWindowSize } from '@office-start/window-utility';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, } from 'react';
import { useDebounce } from 'use-debounce';
import { useScrollRender } from './hooks/useScrollRender';
import { useTeachingBubbleProps } from './hooks/useTeachingBubbleProps';
import { useTuiContent } from './hooks/useTuiContent';
import { useWatchElementUnmount } from './hooks/useWatchElementUnmount';
import { defaultStateValues, teachingUiComponentStateReducer, } from './teachingUiComponentStateReducer';
import { logTuiComplete, logTuiFeature, logTuiImpression, } from './tuiInstrumentation';
var TeachingUiComponent = function (_a) {
    var elementMap = _a.elementMap, customActionProps = _a.customActionProps, scrollContentRef = _a.scrollContentRef, topOffset = _a.topOffset, onLinkClick = _a.onLinkClick, activeSequence = _a.activeSequence, reloadShellElements = _a.reloadShellElements;
    var config = useTeachingUiConfig().data;
    var componentContext = useOfficeComponentContext().data;
    var currentRoute = useCurrentRoute();
    var firstCalloutRendered = useRef(false);
    var _b = activeSequence !== null && activeSequence !== void 0 ? activeSequence : {}, steps = _b.callouts, markSequenceAsSeen = _b.markSequenceAsSeenFn, shouldUpdateNewUserState = _b.shouldUpdateNewUserState, startingIndex = _b.startingIndex;
    /**
     * Teaching UI State
     *
     * currentStepIndex (0 indexed): Raw index of the current step in the sequence
     *
     * displayIndex (1 indexed): Index of the current step in the sequence thats displayed to the user
     * in the N of N format. Ex: 1 of 3.
     */
    var _c = __read(useReducer(teachingUiComponentStateReducer, defaultStateValues), 2), _d = _c[0], status = _d.status, currentStepIndex = _d.currentStepIndex, hasNavigated = _d.hasNavigated, currentCalloutLoaded = _d.currentCalloutLoaded, displayIndex = _d.displayIndex, dispatchTuiState = _c[1];
    useEffect(function () {
        // Apply the starting index when we get one
        if (startingIndex !== undefined) {
            dispatchTuiState({
                type: 'init',
                startingIndex: startingIndex,
            });
        }
    }, [startingIndex, steps]);
    var getFlyoutElement = useCallback(function (targetElementId) {
        return targetElementId.indexOf('AppBar') === 0
            ? elementMap.get('AppBar_Flyout')
            : null;
    }, [elementMap]);
    var isValid = useCallback(function (callout) {
        var _a;
        var element = (_a = elementMap.get(callout.targetElementId)) !== null && _a !== void 0 ? _a : getFlyoutElement(callout.targetElementId);
        var page = currentRoute === null || currentRoute === void 0 ? void 0 : currentRoute.route;
        var isHomePage = page === 'OfficeHome' || page === 'Pwa';
        var calloutPage = callout.page;
        // It is considered valid until on the page
        var notOnCorrectPage = calloutPage === 'Any' ||
            (calloutPage === 'HomePage' && isHomePage)
            ? false
            : callout.page !== (currentRoute === null || currentRoute === void 0 ? void 0 : currentRoute.route);
        var valid = (element !== undefined && element !== null) || notOnCorrectPage;
        return valid;
    }, [elementMap, currentRoute, getFlyoutElement]);
    // Calculate the N of # for the callouts
    var totalSteps = useMemo(function () { var _a; return (_a = steps === null || steps === void 0 ? void 0 : steps.filter(function (callout) { return isValid(callout); }).length) !== null && _a !== void 0 ? _a : -1; }, [steps, isValid]);
    // Get current callout data
    var currentStep = useMemo(function () { return (totalSteps > 0 ? steps === null || steps === void 0 ? void 0 : steps[currentStepIndex] : undefined); }, [steps, currentStepIndex, totalSteps]);
    var setTuiNewUserState = useCallback(function (dismissType) {
        if (config && shouldUpdateNewUserState) {
            var CompletedFirstRun = !!dismissType &&
                (dismissType === 'closed' || dismissType === 'completed');
            updateTuiNewUserState({
                CompletedFirstRun: CompletedFirstRun,
                StepsCompleted: currentStepIndex,
                Version: config === null || config === void 0 ? void 0 : config.version,
            });
            if (CompletedFirstRun) {
                markSequenceAsSeen === null || markSequenceAsSeen === void 0 ? void 0 : markSequenceAsSeen();
            }
        }
    }, [
        currentStepIndex,
        config,
        shouldUpdateNewUserState,
        markSequenceAsSeen,
    ]);
    // Get anchorElement and onLoadAction for the element
    // from the shared element state
    var _e = useMemo(function () {
        var _a, _b;
        if (currentStep && elementMap) {
            return ((_b = (_a = elementMap.get(currentStep.targetElementId)) !== null && _a !== void 0 ? _a : getFlyoutElement(currentStep.targetElementId)) !== null && _b !== void 0 ? _b : {
                element: null,
            });
        }
        return { element: null };
    }, [currentStep, elementMap, getFlyoutElement]), anchorElement = _e.element, onLoadAction = _e.onLoadAction;
    var _f = currentStep !== null && currentStep !== void 0 ? currentStep : {}, directionalHint = _f.directionalHint, stepImpressionEvent = _f.impressionEvent;
    // Callback to track when the first callout is shown to the user
    // so we can record that it was actually shown
    var onRefChange = useCallback(function (base) {
        if (!firstCalloutRendered.current && (base === null || base === void 0 ? void 0 : base.rootElement)) {
            firstCalloutRendered.current = true;
            // Mark callouts as seen
            if (!shouldUpdateNewUserState) {
                // We only want to mark the entire sequence as seen
                // when it is not the new user experience
                markSequenceAsSeen === null || markSequenceAsSeen === void 0 ? void 0 : markSequenceAsSeen();
            }
        }
    }, [markSequenceAsSeen, shouldUpdateNewUserState]);
    // Navigate to the correct page if we aren't already on it
    var updatePage = useCallback(function (isMounted) {
        if (!isMounted())
            return;
        var page = currentStep === null || currentStep === void 0 ? void 0 : currentStep.page;
        if (page &&
            page !== 'Any' &&
            !hasNavigated &&
            // We shouldn't navigate for the first step
            displayIndex !== 1) {
            var calloutRoute = page !== 'HomePage'
                ? page
                : (componentContext === null || componentContext === void 0 ? void 0 : componentContext.workload) === OfficeWorkload.Hwa
                    ? 'Pwa'
                    : 'OfficeHome';
            var route = componentContext === null || componentContext === void 0 ? void 0 : componentContext.router.getCurrentRoute();
            if ((route === null || route === void 0 ? void 0 : route.route) !== calloutRoute) {
                componentContext === null || componentContext === void 0 ? void 0 : componentContext.router.navigateTo({
                    route: calloutRoute,
                });
                dispatchTuiState({ type: 'setNavigation' });
            }
        }
        else if (status === 'active' &&
            currentStep &&
            currentStepIndex > 0 &&
            !hasNavigated &&
            !isValid(currentStep)) {
            // Skip invalid
            dispatchTuiState({ type: 'skip' });
        }
    }, [
        currentStep,
        componentContext,
        hasNavigated,
        displayIndex,
        status,
        currentStepIndex,
        isValid,
    ]);
    // Make sure we only route when the component is mounted
    useIsMounted(updatePage);
    var onComplete = useCallback(function (dismissType) {
        if (currentStep) {
            logTuiComplete(dismissType, currentStep);
        }
        setTuiNewUserState(dismissType);
        dispatchTuiState({ type: 'complete' });
    }, [currentStep, setTuiNewUserState]);
    var isActive = useCallback(function () { return !!currentStep; }, [currentStep]);
    var elementOffscreen = useScrollRender(isActive, topOffset, anchorElement, scrollContentRef);
    // Update the first run state index when it changes
    useEffect(function () {
        if (currentStep && currentStepIndex != -1) {
            setTuiNewUserState();
        }
    }, [currentStep, setTuiNewUserState, currentStepIndex]);
    var _g = useTeachingBubbleProps({
        currentStep: currentStep,
        displayIndex: displayIndex,
        totalSteps: totalSteps,
        onComplete: onComplete,
        customActionProps: customActionProps,
        componentContext: componentContext,
        next: function () { return dispatchTuiState({ type: 'next' }); },
        previous: function () { return dispatchTuiState({ type: 'previous' }); },
    }), primaryButtonProps = _g.primaryButtonProps, secondaryButtonProps = _g.secondaryButtonProps, imageProps = _g.imageProps, teachingBubbleClassname = _g.teachingBubbleClassname, closeButtonAriaLabel = _g.closeButtonAriaLabel;
    // Fetch localized content for the current step
    var content = useTuiContent(displayIndex, totalSteps, currentStep);
    useLayoutEffect(function () {
        if (anchorElement &&
            content &&
            stepImpressionEvent &&
            currentStep &&
            !currentCalloutLoaded) {
            // Make sure the element is in view
            var clientRect = anchorElement.getBoundingClientRect();
            if (scrollContentRef === null || scrollContentRef === void 0 ? void 0 : scrollContentRef.current) {
                var scrollBox = scrollContentRef.current.getBoundingClientRect();
                if (clientRect.top > scrollBox.bottom ||
                    clientRect.bottom < scrollBox.top) {
                    scrollContentRef.current.scrollTo({
                        top: clientRect.top - 55,
                        behavior: 'smooth',
                    });
                }
            }
            // Do load action for element when the element is visible
            onLoadAction === null || onLoadAction === void 0 ? void 0 : onLoadAction();
            // Log callout impression
            logTuiImpression(stepImpressionEvent);
            logTuiFeature(stepImpressionEvent, {
                currentStep: currentStep,
            });
            reloadShellElements === null || reloadShellElements === void 0 ? void 0 : reloadShellElements();
            dispatchTuiState({ type: 'setLoaded' });
        }
    }, [
        anchorElement,
        onLoadAction,
        content,
        dispatchTuiState,
        currentCalloutLoaded,
        stepImpressionEvent,
        currentStep,
        elementOffscreen,
        scrollContentRef,
        reloadShellElements,
    ]);
    // If the target element becomes unmounted (by routing, etc)
    // we want to dismiss the Tui
    useWatchElementUnmount(currentStepIndex, anchorElement, onComplete);
    var isFirstRunVisible = useFirstRunVisibilityStore(function (state) { return state.isVisible; });
    var onDismiss = useCallback(function () {
        if (currentStep === null || currentStep === void 0 ? void 0 : currentStep.onDismiss) {
            currentStep.onDismiss();
        }
        onComplete('closed');
    }, [currentStep, onComplete]);
    // Dismiss TUI on shell flex pane open;
    var shim = useShellShim();
    useEffect(function () {
        var _a;
        var callback = (_a = shim.shellShim) === null || _a === void 0 ? void 0 : _a.FlexPane.OnFlexPaneVisibilityChanged(function () {
            if (currentStep) {
                onDismiss();
            }
        });
        return function () {
            if (callback && shim.shellShim) {
                shim.shellShim.FlexPane.RemoveOnFlexPaneVisibilityChanged(callback);
            }
        };
    }, [currentStep, shim, onDismiss]);
    var anchorElementStale = useMemo(function () {
        return ((anchorElement === null || anchorElement === void 0 ? void 0 : anchorElement.clientHeight) === 0 ||
            (anchorElement === null || anchorElement === void 0 ? void 0 : anchorElement.clientWidth) === 0);
    }, [anchorElement === null || anchorElement === void 0 ? void 0 : anchorElement.clientHeight, anchorElement === null || anchorElement === void 0 ? void 0 : anchorElement.clientWidth]);
    useLayoutEffect(function () {
        var _a;
        if (((_a = currentStep === null || currentStep === void 0 ? void 0 : currentStep.targetElementId) === null || _a === void 0 ? void 0 : _a.includes('Shell')) &&
            anchorElementStale) {
            // Sometimes the shell elements can become stale and need a refresh
            reloadShellElements === null || reloadShellElements === void 0 ? void 0 : reloadShellElements();
        }
    }, [anchorElementStale, currentStep, reloadShellElements]);
    var size = useSingletonWindowSize();
    var _h = __read(useDebounce(size, 200), 1), debouncedSize = _h[0];
    return anchorElement &&
        content &&
        !elementOffscreen &&
        !anchorElementStale &&
        !isFirstRunVisible ? (
    // TODO: Replace with custom fluent v9 callout
    // Native v9 fluent v9 callout work will not start till q3/q4
    React.createElement(TeachingBubble
    // Change key on screen size change to force a position recalculate
    , { 
        // Change key on screen size change to force a position recalculate
        key: debouncedSize.width.toString() + debouncedSize.height.toString(), ref: onRefChange, calloutProps: {
            id: 'tui-callout',
            role: 'alertdialog',
            'aria-live': 'assertive',
            ariaLabelledBy: 'title',
            ariaDescribedBy: 'content',
            className: teachingBubbleClassname,
            preventDismissOnEvent: function () { return true; },
            directionalHint: directionalHint
                ? DirectionalHint[directionalHint]
                : undefined,
            styles: {
                beak: {
                    background: 'inherit',
                    backgroundColor: 'inherit',
                },
                beakCurtain: {
                    background: 'inherit',
                    backgroundColor: 'inherit',
                },
            },
        }, target: anchorElement, primaryButtonProps: primaryButtonProps, secondaryButtonProps: secondaryButtonProps, onDismiss: onDismiss, hasCloseButton: true, closeButtonAriaLabel: closeButtonAriaLabel, illustrationImage: imageProps, focusTrapZoneProps: { disabled: false }, isClickableOutsideFocusTrap: true, footerContent: content.footer, headline: content.title, styles: {
            body: {
                background: 'inherit',
                backgroundColor: 'inherit',
            },
            bodyContent: {
                background: 'inherit',
                backgroundColor: 'inherit',
                position: 'relative',
            },
            content: {
                background: 'inherit',
                backgroundColor: 'inherit',
                color: 'inherit',
            },
            headline: {
                color: 'inherit',
            },
            subText: {
                color: 'inherit',
            },
            closeButton: {
                color: 'inherit',
            },
            footer: {
                color: 'inherit',
            },
        } },
        React.createElement(Text, null, content.text),
        content.urlDisplayText && content.url ? (React.createElement("pre", null,
            React.createElement(Link, { appearance: "subtle", href: content.url, target: "_blank", style: { color: 'inherit' }, onClick: onLinkClick }, content.urlDisplayText))) : null)) : null;
};
export default TeachingUiComponent;
//# sourceMappingURL=TeachingUiComponent.js.map